'use server'

/**
 * The enquiry form's Server Action, shared by the homepage band and the contact page.
 *
 * Order matters and is the whole of the design: the cheap refusals come first, and the
 * only outcome a bot can observe is success. A filled honeypot returns the same state as
 * a real send, with nothing sent and nothing logged beyond a warning. Only a submission
 * that passes everything reaches the backend, which validates again (it is reachable
 * directly), renders the two emails and queues them.
 *
 * Every export here must be an async function; the state type, the vocabulary, the
 * limiter and the backend call live in sibling modules for that reason.
 */

import { headers } from 'next/headers'
import { z } from 'zod'
import { postEnquiry } from './backend'
import {
  type EnquirySource,
  HONEYPOT_FIELD,
  isEnquirySource,
} from './enquiry-fields'
import { enquiryRateLimiter } from './enquiry-rate-limit'
import { newEnquiryReference } from './enquiry-reference'
import { type EnquiryInput, enquirySchema } from './enquiry-schema'
import type {
  EnquiryFieldErrors,
  EnquiryState,
  EnquiryValues,
} from './enquiry-state'

function text(form: FormData, name: string): string {
  const value = form.get(name)
  return typeof value === 'string' ? value : ''
}

function readValues(form: FormData): EnquiryValues {
  return {
    firstName: text(form, 'firstName'),
    lastName: text(form, 'lastName'),
    email: text(form, 'email'),
    company: text(form, 'company'),
    kind: text(form, 'kind'),
    message: text(form, 'message'),
    consent: text(form, 'consent'),
  }
}

function readSource(form: FormData): EnquirySource {
  const source = text(form, 'source')
  return isEnquirySource(source) ? source : 'wysehub-homepage'
}

/**
 * The visitor's address, as Traefik reports it.
 *
 * Traefik is the only thing in front of this server and does not trust a client-supplied
 * `X-Forwarded-For`, so the first hop is the visitor. Nothing else on the request is
 * worth keying a limit on. Empty when neither header is there, which only happens off
 * the proxy; the limiter then keys on `unknown` and the backend is told nothing.
 */
async function clientIp(): Promise<string> {
  const headerList = await headers()
  const forwarded = headerList.get('x-forwarded-for')?.split(',')[0]?.trim()
  return forwarded || headerList.get('x-real-ip') || ''
}

/** The public host the request arrived on, for the backend to resolve the brand by. */
async function publicHost(): Promise<string> {
  const headerList = await headers()
  const host =
    headerList.get('x-forwarded-host') || headerList.get('host') || ''
  return host.split(',')[0]?.trim() ?? ''
}

/** The first message per field: one line under the control, not a list. */
function fieldErrors(error: z.ZodError<EnquiryInput>): EnquiryFieldErrors {
  const flattened = z.flattenError(error).fieldErrors
  const out: Partial<Record<keyof EnquiryInput, string>> = {}
  for (const field of Object.keys(flattened) as (keyof EnquiryInput)[]) {
    const first = flattened[field]?.[0]
    if (first) out[field] = first
  }
  return out
}

export async function submitEnquiry(
  _previous: EnquiryState,
  form: FormData,
): Promise<EnquiryState> {
  const values = readValues(form)
  const source = readSource(form)
  const ip = await clientIp()
  const limitKey = ip || 'unknown'

  if (text(form, HONEYPOT_FIELD) !== '') {
    console.warn('enquiry.honeypot', { ip: limitKey, source })
    return { status: 'sent', reference: newEnquiryReference() }
  }

  if (!enquiryRateLimiter.allow(limitKey)) {
    console.warn('enquiry.rate_limited', { ip: limitKey, source })
    return { status: 'failed', reason: 'rate-limited', values }
  }

  const parsed = enquirySchema.safeParse(values)
  if (!parsed.success) {
    return { status: 'invalid', errors: fieldErrors(parsed.error), values }
  }

  const reference = newEnquiryReference()

  let status: number | undefined
  let requestId: string | undefined
  try {
    const response = await postEnquiry(
      {
        reference,
        first_name: parsed.data.firstName,
        last_name: parsed.data.lastName,
        email: parsed.data.email,
        company: parsed.data.company || null,
        kind: parsed.data.kind,
        message: parsed.data.message,
        consent: true,
        source,
        brand: 'wysehub',
      },
      { ip, host: await publicHost() },
    )
    status = response.status
    requestId = response.headers.get('x-request-id') ?? undefined
    if (response.ok) {
      // The reference and the kind, never the person: this line is the only record
      // the frontend keeps, and the privacy notice says the two emails are the only copies.
      console.info('enquiry.sent', {
        reference,
        source,
        kind: parsed.data.kind,
      })
      return { status: 'sent', reference }
    }
  } catch (error) {
    console.error('enquiry.upstream_unreachable', {
      reference,
      error: String(error),
    })
    return { status: 'failed', reason: 'unavailable', values }
  }

  console.error('enquiry.backend_failed', { reference, status, requestId })
  return { status: 'failed', reason: 'unavailable', values }
}
